import AppCard from '@nxsys/components/AppCard';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useGetPensionContribution } from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { formatStatus, showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Skeleton } from 'antd';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const { palette } = defaultTheme.theme;

// Ireland: each column is stacked MFF (NAERSA) + private pension, same lines
// as the payslip and the YTD card (NX-6033). Both keep the column colour.
// Employer MFF is the lighter green; employee MFF is a darker amber, since
// warning-300 is already too pale for a lighter shade to show (NX-6033 C4).
const irelandSegments = [
  {
    key: 'mffEEPension',
    stackId: 'employee',
    label: 'Employee MFF',
    color: '#E9B21C',
  },
  {
    key: 'privateEEPension',
    stackId: 'employee',
    label: 'Employee Pension',
    color: palette.warning[300],
  },
  {
    key: 'mffERPension',
    stackId: 'employer',
    label: 'Employer MFF',
    color: '#268D74',
  },
  {
    key: 'privateERPension',
    stackId: 'employer',
    label: 'Employer Pension',
    color: palette.primary[600],
  },
] as const;

type IrelandSegmentKey = (typeof irelandSegments)[number]['key'];

// A segment is the top of its column when every segment stacked above it is 0
const isTopSegment = (
  data: Record<IrelandSegmentKey, number>,
  key: IrelandSegmentKey,
) => {
  const index = irelandSegments.findIndex((segment) => segment.key === key);
  const { stackId } = irelandSegments[index];
  return irelandSegments
    .slice(index + 1)
    .every((segment) => segment.stackId !== stackId || !data[segment.key]);
};

const EmployeePensionContribution = () => {
  const { payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const subcontractorId = (user?.subcontractorId as any)?.id;

  const {
    run: getPensionContribution,
    value: pensionValue,
    loading,
  } = useGetPensionContribution();

  useEffect(() => {
    if (subcontractorId) {
      getPensionContribution(subcontractorId);
    }
  }, [subcontractorId]);

  const pensionData = { ...pensionValue?.data };

  // Transform monthlyContributions to chart format (tax year order: Apr-Mar)
  const monthlyData = useMemo(() => {
    if (!pensionData?.monthlyContributions) {
      return [];
    }

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    // Map data using the month field from API
    const mappedData = pensionData.monthlyContributions.map((contribution) => ({
      month: monthNames[contribution.month - 1],
      monthNumber: contribution.month,
      taxMonth: contribution.taxMonth,
      employeeContribution:
        contribution.mainEEPension + contribution.avcEEPension,
      employerContribution:
        contribution.mainERPension + contribution.avcERPension,
      mffEEPension: contribution.mffEEPension ?? 0,
      privateEEPension: contribution.privateEEPension ?? 0,
      mffERPension: contribution.mffERPension ?? 0,
      privateERPension: contribution.privateERPension ?? 0,
    }));

    // Sort by taxMonth to display in tax year order (Apr to Mar)
    return mappedData.sort((a, b) => a.taxMonth - b.taxMonth);
  }, [pensionData?.monthlyContributions]);

  // Use main pension values from currentYTD for totals
  const totalEeContribution = pensionData?.currentYTD?.mainEEPension ?? 0;
  const totalErContribution = pensionData?.currentYTD?.mainERPension ?? 0;

  const renderIrelandSplit = (stackId: 'employee' | 'employer') =>
    irelandSegments
      .filter((segment) => segment.stackId === stackId)
      .map(({ key, label, color }) => (
        <div
          key={key}
          className="flex items-center gap-x-[6px] mt-[4px] text-[12px] text-neutral-500"
        >
          <div
            className="w-[8px] h-[8px] rounded-[2px]"
            style={{ backgroundColor: color }}
          />
          <span>{label}:</span>
          <span className="font-[500] text-neutral-700">
            {formatMoney(
              pensionData?.currentYTD?.[key] ?? 0,
              showCurrencyByCountry(payrollCountry),
            )}
          </span>
        </div>
      ));

  // Get current date for "Updated" field
  const currentDate = useMemo(() => {
    const today = new Date();
    return today.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }, []);

  // Loading state
  if (loading && !pensionValue) {
    return (
      <AppCard className="h-full">
        <div className="p-[20px]">
          <Skeleton active />
        </div>
      </AppCard>
    );
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const monthData = monthlyData.find((d) => d.month === label);
      if (!monthData) return null;

      // Get full month name
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      const monthIndex = monthData.monthNumber - 1;
      const year =
        pensionData?.monthlyContributions?.find(
          (c) => c.month === monthData.monthNumber,
        )?.year ?? new Date().getFullYear();
      const fullMonthName = `${monthNames[monthIndex]} ${year}`;

      if (isIreland) {
        return (
          <div className="bg-neutral-900 text-white rounded-[8px] p-[12px] shadow-lg">
            <div className="text-[14px] font-[500] mb-[8px]">
              {fullMonthName}
            </div>
            {irelandSegments.map(({ key, label, color }) => (
              <div
                key={key}
                className="flex items-center gap-x-[8px] mb-[4px] last:mb-0"
              >
                <div
                  className="w-[8px] h-[8px] rounded-[2px]"
                  style={{ backgroundColor: color }}
                />
                <div className="text-[14px] font-[400]">{label}:</div>
                <div className="text-[14px] font-[600]">
                  {formatMoney(
                    monthData[key],
                    showCurrencyByCountry(payrollCountry),
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      }

      return (
        <div className="bg-neutral-900 text-white rounded-[8px] p-[12px] shadow-lg">
          <div className="text-[14px] font-[500] mb-[8px]">{fullMonthName}</div>
          <div className="flex items-center gap-x-[8px] mb-[4px]">
            <div
              className="w-[8px] h-[8px] rounded-[2px]"
              style={{
                backgroundColor: defaultTheme.theme.palette.warning[300],
              }}
            />
            <div className="text-[14px] font-[400]">Employee Contribution:</div>
            <div className="text-[14px] font-[600]">
              {formatMoney(
                monthData.employeeContribution,
                showCurrencyByCountry(payrollCountry),
              )}
            </div>
          </div>
          <div className="flex items-center gap-x-[8px]">
            <div
              className="w-[8px] h-[8px] rounded-[2px]"
              style={{
                backgroundColor: defaultTheme.theme.palette.primary[600],
              }}
            />
            <div className="text-[14px] font-[400]">Employer Contribution:</div>
            <div className="text-[14px] font-[600]">
              {formatMoney(
                monthData.employerContribution,
                showCurrencyByCountry(payrollCountry),
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <AppCard className="h-full">
      <div className="p-[20px]">
        <div className="flex items-center justify-between w-full">
          <div className="flex-1">
            <div className="flex items-center gap-x-[6px] mb-[6px]">
              <div className="text-[16px] font-[500]">Pension Contribution</div>
              {pensionData?.isPensionActive && (
                <AppTagStatus className={`tag-popup ${formatStatus('Active')}`}>
                  Active
                </AppTagStatus>
              )}
            </div>
            <div className="text-[12px] font-[400] text-neutral-500">
              This chart shows how your pension has grown each month.
            </div>
          </div>
          <div className="flex items-center gap-x-[8px] border border-neutral-200 rounded-[8px] p-[6px] w-fit">
            <div className="w-[8px] h-[8px] bg-primary-600 rounded-full"></div>
            <div className="text-[14px] font-[500] text-neutral-700">
              Updated: {currentDate}
            </div>
          </div>
        </div>
        <div className="mt-[24px]">
          <div className="h-[150px] mb-[24px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={monthlyData}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                barCategoryGap="20%"
              >
                <XAxis
                  dataKey="month"
                  tick={{
                    fontSize: 12,
                    fill: defaultTheme.theme.palette.neutral[700],
                  }}
                  axisLine={{ stroke: defaultTheme.theme.palette.neutral[200] }}
                />
                <YAxis
                  tick={{
                    fontSize: 12,
                    fill: defaultTheme.theme.palette.neutral[700],
                  }}
                  axisLine={{ stroke: defaultTheme.theme.palette.neutral[200] }}
                  tickFormatter={(value) => formatMoney(value, '')}
                  domain={[0, 'auto']}
                />
                <Tooltip content={<CustomTooltip />} />
                {isIreland ? (
                  irelandSegments.map(({ key, stackId, color }) => (
                    <Bar key={key} dataKey={key} stackId={stackId} fill={color}>
                      {/* Round only the top segment of each column, as before */}
                      {monthlyData.map((data) => (
                        <Cell
                          key={data.month}
                          // Cell types radius as a number, but the bar's
                          // Rectangle takes the per-corner array
                          radius={
                            isTopSegment(data, key)
                              ? ([4, 4, 0, 0] as any)
                              : undefined
                          }
                        />
                      ))}
                    </Bar>
                  ))
                ) : (
                  <>
                    <Bar
                      dataKey="employeeContribution"
                      fill={defaultTheme.theme.palette.warning[300]}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="employerContribution"
                      fill={defaultTheme.theme.palette.primary[600]}
                      radius={[4, 4, 0, 0]}
                    />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-x-[16px]">
            <div className="border border-[#CDDDD9] rounded-[8px] py-[12px] px-[16px]">
              <div className="flex gap-x-[8px]">
                <div className="w-[12px] h-[12px] bg-warning-300 rounded-[2px]" />
                <div>
                  <div className="text-[16px] font-[500] text-neutral-700">
                    {formatMoney(
                      totalEeContribution,
                      showCurrencyByCountry(payrollCountry),
                    )}
                  </div>
                  <div className="text-[12px] font-[400] text-neutral-500">
                    Total Employee Contribution
                  </div>
                  {isIreland && renderIrelandSplit('employee')}
                </div>
              </div>
            </div>
            <div className="border border-[#CDDDD9] rounded-[8px] py-[12px] px-[16px]">
              <div className="flex gap-x-[8px]">
                <div className="w-[12px] h-[12px] bg-primary-600 rounded-[2px]" />
                <div>
                  <div className="text-[16px] font-[500] text-neutral-700">
                    {formatMoney(
                      totalErContribution,
                      showCurrencyByCountry(payrollCountry),
                    )}
                  </div>
                  <div className="text-[12px] font-[400] text-neutral-500">
                    Total Employer Contribution
                  </div>
                  {isIreland && renderIrelandSplit('employer')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppCard>
  );
};

export default EmployeePensionContribution;
